import { Text } from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import { useFundedOfferingsQuery } from '@r-client/republic/data/offerings';
import { QueryError } from '@r-client/republic/feature/errors';
import { OfferingDealCard } from '@r-client/republic/feature/offering-deal-card';
import {
  DealRailSection,
  DealRailSkeleton,
} from '@r-client/republic/ui/offerings';
import { ASSET_TYPES } from '@r-client/shared/data/analytics';
import { isInitialQueryLoading } from '@r-client/shared/util/core';

import { useInvestmentOpportunitiesAnalytics } from '../../hooks/use-investment-opportunities-analytics';
import {
  EMPTY_MESSAGE,
  FUNDED_QUERY_VARIABLES,
  HEADING,
  LOADING_LABEL,
  SEE_ALL_HREF,
} from './constants';

/**
 * Funded ventures section of the /investment-opportunities page.
 *
 * Unlike the growth-stage grid, the query lives here rather than in the rail:
 * the heading needs `totalCount` and the rail needs `nodes`, and both come from
 * the same request.
 *
 * `observer` is required — `BaseQueryModel` only installs its variables autorun
 * once it is MobX-observed, so without this wrapper the section would sit on the
 * skeleton forever. Note `mobx/missing-observer` is not enabled for this
 * directory, so nothing but the spec guards it.
 */
export const FundedVentures = observer(function FundedVentures() {
  const offeringsQuery = useFundedOfferingsQuery({
    variables: FUNDED_QUERY_VARIABLES,
    notifyOnNetworkStatusChange: true,
  });
  const { seeAllClicked, assetCardClicked, assetFollowed, assetUnfollowed } =
    useInvestmentOpportunitiesAnalytics();

  const offerings = offeringsQuery.data?.offeringsSearch.nodes ?? [];

  // Both "See all" affordances — the header link and the rail's terminal tile —
  // lead to the same place, so they report the same event.
  const onSeeAllClick = () => seeAllClicked(ASSET_TYPES.FUNDED_OFFERING);

  const renderFallback = () => {
    if (isInitialQueryLoading(offeringsQuery))
      return <DealRailSkeleton ariaLabel={LOADING_LABEL} />;

    if (offeringsQuery.isRejected)
      return <QueryError queries={[offeringsQuery]} showRefetch />;

    if (!offerings.length)
      return (
        <Text size="body-md" variant="text-secondary">
          {EMPTY_MESSAGE}
        </Text>
      );

    return undefined;
  };

  return (
    <DealRailSection
      title={HEADING}
      // Simply absent until the query resolves, so the heading never reflows
      // from a placeholder.
      count={offeringsQuery.data?.offeringsSearch.totalCount}
      seeAllHref={SEE_ALL_HREF}
      onSeeAllClick={onSeeAllClick}
      fallback={renderFallback()}
    >
      {offerings.map((offering) => (
        <OfferingDealCard
          key={offering.id}
          offering={offering}
          onCardClick={() =>
            assetCardClicked(ASSET_TYPES.FUNDED_OFFERING, offering.slug)
          }
          onFollow={() =>
            assetFollowed(ASSET_TYPES.FUNDED_OFFERING, offering.slug)
          }
          onUnfollow={() =>
            assetUnfollowed(ASSET_TYPES.FUNDED_OFFERING, offering.slug)
          }
        />
      ))}
    </DealRailSection>
  );
});
